import React, { useState } from "react";
import { publicApi } from "../api";
import PasswordInput from "./PasswordInput";
import SuDatePicker from "./SuDatePicker";

/**
 * Two ways to find an existing membership:
 *  1. Membership / card number (all providers).
 *  2. Name + DOB (IGT only) — for members who've lost their card.
 *
 * Both paths re-use the same PIN-gate sub-step after a hit, then hand
 * the resolved member back up to the parent as a renewal. A banned /
 * self-excluded result returned from either endpoint surfaces an
 * unambiguous "contact the venue" message and ends the flow.
 */
export default function StepLookup({ slug, venue, onResult, onBack }) {
  const isIgt = (venue?.provider_type === "igt");
  const isCircleScan = (venue?.provider_type === "circlescan");
  const [mode, setMode] = useState("number"); // "number" | "name"

  // Number-lookup state
  const [value, setValue] = useState("");

  // Name-lookup state
  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [dob,       setDob]       = useState("");

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [blocked, setBlocked] = useState("");         // banned / self-excluded message
  const [notFound, setNotFound] = useState(false);

  // PIN verification sub-step
  const [foundMember, setFoundMember] = useState(null);
  const [pinStep,     setPinStep]     = useState(false);
  const [pin,         setPin]         = useState("");
  const [pinError,    setPinError]    = useState("");
  const [pinLoading,  setPinLoading]  = useState(false);

  const pinRequired = venue?.pin_required;

  // Common post-lookup handling: proceed to PIN step or hand back
  // directly (if the venue has pin_required=false, which currently
  // never happens but the flag is there for future flexibility).
  const afterFound = (member) => {
    if (pinRequired) {
      setFoundMember(member);
      setPinStep(true);
      setPin(""); setPinError("");
    } else {
      onResult(true, member);
    }
  };

  // Pick a friendly message out of a backend error payload. The backend
  // returns 403 + { banned | self_excluded, error } for restricted
  // members; we surface that directly instead of a generic "unable to
  // connect" so the user knows to contact the venue.
  const readErr = (err) => {
    const msg = String(err?.message || "");
    // Restricted (banned / self-excluded) members come back as a coded
    // 403 with a generic, privacy-preserving message. Treat it as a hard
    // stop. The legacy banned/self_excluded text checks stay for
    // backward-compat with an older backend.
    if (err?.code === "MEMBER_RESTRICTED" || err?.data?.restricted) return { blocked: msg };
    if (/self.?excluded|self_excluded/i.test(msg)) return { blocked: msg };
    if (/banned/i.test(msg)) return { blocked: msg };
    if (msg && msg.length < 200) return { error: msg };
    return { error: "Unable to connect. Please try again." };
  };

  const handleNumberLookup = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setLoading(true); setError(""); setBlocked(""); setNotFound(false);
    try {
      const data = await publicApi.lookupMember(slug, value.trim());
      if (data.found) afterFound(data.member);
      else setNotFound(true);
    } catch (err) {
      const r = readErr(err);
      if (r.blocked) setBlocked(r.blocked); else setError(r.error);
    } finally {
      setLoading(false);
    }
  };

  const handleNameLookup = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !dob) return;
    setLoading(true); setError(""); setBlocked(""); setNotFound(false);
    try {
      const data = await publicApi.lookupMemberByName(slug, {
        firstName: firstName.trim(),
        lastName:  lastName.trim(),
        dob,
      });
      if (data.found) afterFound(data.member);
      else setNotFound(true);
    } catch (err) {
      const r = readErr(err);
      if (r.blocked) setBlocked(r.blocked); else setError(r.error);
    } finally {
      setLoading(false);
    }
  };

  const handlePinVerify = async (e) => {
    e.preventDefault();
    if (!pin || pin.length !== 4) { setPinError("Please enter your 4-digit PIN."); return; }
    setPinLoading(true); setPinError("");
    try {
      const memberNumber = foundMember.MEMBERNUMBER || foundMember.memberNumber || value.trim();
      const cardNumber   = foundMember.CARDNUMBER || foundMember.cardNumber || "";
      const result = await publicApi.verifyPin(slug, memberNumber, pin, cardNumber);
      // Forward any pre-fill payloads from the PIN response so the
      // parent can seed downstream steps (e.g. comms-prefs) with the
      // member's current state. We pass them as a third argument so
      // existing onResult callers that ignore extras keep working.
      if (result.valid) onResult(true, foundMember, { commsPrefs: result.commsPrefs || null });
      else setPinError("Incorrect PIN. Please try again.");
    } catch {
      setPinError("PIN verification failed. Please try again.");
    } finally {
      setPinLoading(false);
    }
  };

  const handlePinBack = () => {
    setPinStep(false); setFoundMember(null); setPin(""); setPinError("");
  };

  // ─── PIN verification sub-step ────────────────
  if (pinStep) {
    return (
      <>
        <h2 className="step-title">Verify your identity</h2>
        <p className="step-sub">Enter the 4-digit PIN associated with your membership to continue.</p>

        {pinError && <div className="su-alert error">⚠ {pinError}</div>}

        <form onSubmit={handlePinVerify} className="lookup-wrap">
          <div className="su-field">
            <label className="su-label">Membership PIN <span className="su-required">*</span></label>
            <PasswordInput
              className="su-input"
              inputMode="numeric"
              pattern="\d{4}"
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="••••"
              autoFocus
              autoComplete="off"
            />
          </div>

          <div className="su-btn-row">
            <button type="button" className="su-btn su-btn-outline" onClick={handlePinBack}>← Back</button>
            <button type="submit" className="su-btn su-btn-primary" disabled={pinLoading || pin.length !== 4}>
              {pinLoading ? "Verifying…" : "Verify PIN →"}
            </button>
          </div>
        </form>
      </>
    );
  }

  // ─── Banned / self-excluded — hard stop, no fallback ─────
  if (blocked) {
    return (
      <>
        <h2 className="step-title">Please contact the venue</h2>
        <div className="su-alert error" style={{ flexDirection: "column", alignItems: "flex-start", gap: "0.5rem" }}>
          <span>{blocked}</span>
        </div>
        <div className="su-btn-row">
          <button type="button" className="su-btn su-btn-outline" onClick={onBack}>← Start over</button>
        </div>
      </>
    );
  }

  // ─── Main lookup step ─────────────────────────
  return (
    <>
      <h2 className="step-title">Find your membership</h2>
      <p className="step-sub">
        {mode === "number"
          ? (isCircleScan
              ? "Enter your membership number to find your record."
              : "Enter your card or membership number to pre-fill your details.")
          : "Enter your name and date of birth — we'll look up your existing membership."}
      </p>

      {error && <div className="su-alert error">⚠ {error}</div>}

      {notFound ? (
        <div className="su-alert info" style={{ flexDirection: "column", alignItems: "flex-start", gap: "0.75rem" }}>
          <span>
            {mode === "number"
              ? "No membership found with that number."
              : "We couldn't find a matching membership for that name and date of birth."}
          </span>
          <button className="su-btn su-btn-primary" type="button" onClick={() => onResult(false, null)}>
            Continue as New Member →
          </button>
          <button
            className="su-btn su-btn-outline"
            type="button"
            onClick={() => { setNotFound(false); }}
          >
            Try a different search
          </button>
        </div>
      ) : mode === "number" ? (
        <form onSubmit={handleNumberLookup} className="lookup-wrap">
          <div className="su-field">
            <label className="su-label" htmlFor="su-lookup-number">{isCircleScan ? "Membership Number" : "Card / Membership Number"} <span className="su-required">*</span></label>
            <input
              id="su-lookup-number"
              className="su-input"
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g. 1234567890"
              maxLength={20}
              autoFocus
            />
          </div>

          {isIgt && (
            <div style={{ textAlign: "center", margin: "0.35rem 0 0.25rem" }}>
              <button
                type="button"
                className="su-link-btn"
                onClick={() => { setMode("name"); setError(""); setNotFound(false); }}
              >
                I don't know my membership number
              </button>
            </div>
          )}

          <div className="su-btn-row">
            <button type="button" className="su-btn su-btn-outline" onClick={onBack}>← Back</button>
            <button type="submit" className="su-btn su-btn-primary" disabled={loading || !value.trim()}>
              {loading ? "Searching…" : "Find Member →"}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleNameLookup} className="lookup-wrap">
          <div className="su-field">
            <label className="su-label" htmlFor="su-lookup-first">First name <span className="su-required">*</span></label>
            <input id="su-lookup-first" className="su-input" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoFocus />
          </div>
          <div className="su-field">
            <label className="su-label" htmlFor="su-lookup-last">Last name <span className="su-required">*</span></label>
            <input id="su-lookup-last" className="su-input" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
          <div className="su-field">
            <label className="su-label">Date of birth <span className="su-required">*</span></label>
            <SuDatePicker
              label="Date of birth"
              value={dob}
              onChange={(iso) => setDob(iso)}
              required
              min="1900-01-01"
              max={new Date().toISOString().slice(0, 10)}
            />
          </div>

          <div style={{ textAlign: "center", margin: "0.35rem 0 0.25rem" }}>
            <button
              type="button"
              className="su-link-btn"
              onClick={() => { setMode("number"); setError(""); setNotFound(false); }}
            >
              I have my membership number
            </button>
          </div>

          <div className="su-btn-row">
            <button type="button" className="su-btn su-btn-outline" onClick={onBack}>← Back</button>
            <button
              type="submit"
              className="su-btn su-btn-primary"
              disabled={loading || !firstName.trim() || !lastName.trim() || !dob}
            >
              {loading ? "Searching…" : "Find Member →"}
            </button>
          </div>
        </form>
      )}
    </>
  );
}
